import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { refreshSessionFromCookie } from '@/lib/auth/session';
import { useAuthStore } from '@/stores/auth';
import { generateTraceId } from '@/lib/trace';

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

const baseURL = import.meta.env.VITE_API_BASE_URL || '/api/v1';
const SCOPED_BRANCH_PATHS = new Set([
  '/merchant/receipts',
  '/merchant/summary',
  '/merchant/analytics',
  '/merchant/analytics/pipeline',
  '/merchant/analytics/inventory',
  '/merchant/deals',
  '/merchant/devices/health',
]);

function normalizeRequestPath(url: string | undefined): string | null {
  if (!url) return null;

  const withoutHash = url.split('#')[0] ?? '';
  const withoutQuery = withoutHash.split('?')[0] ?? '';

  if (!withoutQuery) return null;

  try {
    const parsed = new URL(withoutQuery);
    if (parsed.pathname.startsWith('/api/v1/')) {
      return parsed.pathname.slice('/api/v1'.length);
    }
    return parsed.pathname;
  } catch {
    if (withoutQuery.startsWith('/api/v1/')) {
      return withoutQuery.slice('/api/v1'.length);
    }
    return withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
  }
}

function isScopedBranchPath(path: string | null): boolean {
  if (!path) return false;
  return SCOPED_BRANCH_PATHS.has(path);
}

function appendBranchIdParam(
  params: InternalAxiosRequestConfig['params'],
  branchId: string,
): InternalAxiosRequestConfig['params'] {
  if (params instanceof URLSearchParams) {
    if (!params.has('branch_id')) {
      params.set('branch_id', branchId);
    }
    return params;
  }

  if (!params || typeof params !== 'object' || Array.isArray(params)) {
    return { branch_id: branchId };
  }

  if ('branch_id' in params) {
    return params;
  }

  return {
    ...params,
    branch_id: branchId,
  };
}

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
  // The auth endpoints set/rotate the HttpOnly dp_refresh cookie
  // (Path=/api/v1/auth). Credentials mode is required for the cookie
  // to be stored/sent when the API base is cross-origin (prod);
  // same-origin dev is unaffected.
  withCredentials: true,
});

// ---------- Request interceptor: bearer + trace ID ---------- //
apiClient.interceptors.request.use((config) => {
  const authState = useAuthStore.getState();
  const token = authState.accessToken;
  const activeBranchId = authState.activeBranchId;
  const hasAuthorizationHeader = Boolean(
    config.headers.Authorization || config.headers.authorization,
  );

  if (token && !hasAuthorizationHeader) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const path = normalizeRequestPath(config.url);
  if (typeof activeBranchId === 'string' && activeBranchId !== 'all' && isScopedBranchPath(path)) {
    config.params = appendBranchIdParam(config.params, activeBranchId);
  }

  config.headers['X-Trace-Id'] = generateTraceId();
  return config;
});

// ---------- Response interceptor: 401 silent refresh ---------- //
let refreshPromise: Promise<string | null> | null = null;

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const original = error.config as RetryableConfig | undefined;

    // IP-allowlist 403 → bounce to access-denied page (Caddy-served)
    if (status === 403) {
      const data = error.response?.data as { error?: { code?: string } } | undefined;
      if (data?.error?.code === 'IP_NOT_ALLOWED' && typeof window !== 'undefined') {
        window.location.href = '/access-denied';
        return Promise.reject(error);
      }
    }

    if (status === 401 && original && !original._retried) {
      original._retried = true;
      refreshPromise ??= refreshSessionFromCookie().finally(() => {
        refreshPromise = null;
      });
      const newToken = await refreshPromise;
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(original);
      }
    }

    return Promise.reject(error);
  },
);
